import api from '../config/api';
import { partirEmpleadosCierre } from '../utils/partirEmpleadosCierre';

const planillaCierreService = {
    /**
     * Cerrar una planilla - Captura todos los datos y crea el snapshot
     * @param {number} planillaId - ID de la planilla a cerrar
     * @param {Array} empleadosCalculados - Array completo de empleados con datos calculados (incluye configuracion_tareo en cada empleado)
     * @param {boolean} confirmarCierre - Confirmación explícita del cierre
     * @returns {Promise} - Resultado del cierre
     */
    async cerrarPlanilla(planillaId, empleadosCalculados = [], confirmarCierre = true, onProgress = null) {
        try {
            console.log(`🔐 Iniciando cierre de planilla ID: ${planillaId}...`);
            console.log(`📊 Enviando ${empleadosCalculados.length} empleados calculados al backend`);

            const empleadosConConfiguracion = empleadosCalculados.filter(emp => emp.configuracion_tareo);
            console.log(`⚙️ Empleados con configuración de tareos: ${empleadosConConfiguracion.length}/${empleadosCalculados.length}`);

            const lotes = partirEmpleadosCierre(empleadosCalculados);
            const totalLotes = Math.max(lotes.length, 1);
            const sesionId = `cierre-${planillaId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            console.log(`📦 Cierre partido en ${totalLotes} lote(s)`);

            let ultimaRespuesta = null;
            for (let i = 0; i < totalLotes; i++) {
                const esUltimo = i === totalLotes - 1;
                if (typeof onProgress === 'function') {
                    onProgress({
                        fase: esUltimo ? 'cerrando' : 'enviando',
                        lote: i + 1,
                        total: totalLotes,
                        progreso: Math.round(((i) / totalLotes) * 85),
                    });
                }

                const payload = {
                    confirmar_cierre: confirmarCierre,
                    sesion_id: sesionId,
                    lote_index: i,
                    total_lotes: totalLotes,
                    empleados_calculados: lotes[i] || [],
                };

                const response = await api.post(
                    `/planillas-cierre/${planillaId}/cerrar-lote`,
                    payload,
                    { timeout: esUltimo ? 180000 : 60000 }
                );

                ultimaRespuesta = this.interpretarRespuestaCierre(response, planillaId);
                if (ultimaRespuesta.success && ultimaRespuesta.completo === false) {
                    continue;
                }
                if (!ultimaRespuesta.success) {
                    return ultimaRespuesta;
                }
            }

            if (!ultimaRespuesta || ultimaRespuesta.completo === false) {
                return {
                    success: false,
                    error: 'No se completó el armado del cierre. Vuelve a intentar.',
                };
            }

            if (typeof onProgress === 'function') {
                onProgress({
                    fase: 'listo',
                    lote: totalLotes,
                    total: totalLotes,
                    progreso: 100,
                });
            }

            return ultimaRespuesta;

        } catch (error) {
            console.error('❌ Error de comunicación al cerrar planilla:', error);
            
            // ❌ SI EL BACKEND ENVIÓ UN ERROR HTTP 500 CON DATOS ESTRUCTURADOS
            if (error.response?.status === 500 && error.response?.data?.success === false) {
                console.error('❌ Error HTTP 500 con datos estructurados:', error.response.data);
                return {
                    success: false,
                    error: error.response.data.error || 'Error al cerrar planilla',
                    detalle_error: error.response.data.detalle_error || null,
                    data: error.response.data.data || null,
                    es_error_controlado: true,
                    http_status: 500
                };
            }
            
            if (error.response?.status === 413) {
                return {
                    success: false,
                    error: 'Un lote del cierre sigue siendo demasiado grande para el servidor',
                    details: error.response?.data || null,
                    es_error_controlado: false,
                    http_status: 413
                };
            }

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.message || 
                               error.message || 
                               'Error de comunicación con el servidor';

            return {
                success: false,
                error: errorMessage,
                details: error.response?.data || null,
                es_error_controlado: false,
                http_status: error.response?.status || null
            };
        }
    },

    interpretarRespuestaCierre(response, planillaId) {
        if (response.data && response.data.success === true && response.data.completo === false) {
            return {
                success: true,
                completo: false,
                lotes_recibidos: response.data.lotes_recibidos,
                total_lotes: response.data.total_lotes,
                message: response.data.message || 'Lote recibido',
            };
        }

        if (response.data && response.data.success === true) {
            console.log(`✅ Planilla ${planillaId} cerrada exitosamente:`, response.data);
            return {
                success: true,
                completo: true,
                data: response.data.data,
                message: response.data.message || 'Planilla cerrada exitosamente',
                estadisticas: response.data.data?.estadisticas || null
            };
        }

        if (response.data && response.data.success === false) {
            console.error(`❌ Error en cierre de planilla ${planillaId}:`, response.data);
            return {
                success: false,
                error: response.data.error || 'Error al cerrar planilla',
                detalle_error: response.data.detalle_error || null,
                data: response.data.data || null,
                es_error_controlado: true
            };
        }

        return {
            success: false,
            error: 'Respuesta inesperada del servidor',
            data: response.data || null
        };
    },

    /**
     * Obtener todos los datos de una planilla cerrada
     * @param {number} planillaId - ID de la planilla cerrada
     * @returns {Promise} - Datos completos de la planilla cerrada
     */
    async obtenerPlanillaCerrada(planillaId) {
        try {
            console.log(`📊 Obteniendo datos de planilla cerrada ID: ${planillaId}...`);
            
            const response = await api.get(`/planillas-cierre/${planillaId}/datos`);

            if (response.data && response.data.data) {
                console.log(`✅ Datos de planilla cerrada obtenidos:`, {
                    empleados: response.data.data.length,
                    planillaId: planillaId
                });

                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || 'Datos obtenidos exitosamente'
                };
            }

            return {
                success: false,
                error: 'No se encontraron datos para la planilla cerrada'
            };

        } catch (error) {
            console.error('❌ Error al obtener planilla cerrada:', error);
            
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Error al obtener datos de planilla cerrada';

            return {
                success: false,
                error: errorMessage,
                details: error.response?.data || null
            };
        }
    },

    /**
     * Obtener estadísticas resumidas de una planilla cerrada
     * @param {number} planillaId - ID de la planilla cerrada
     * @returns {Promise} - Estadísticas de la planilla cerrada
     */
    async obtenerEstadisticasPlanillaCerrada(planillaId) {
        try {
            console.log(`📈 Obteniendo estadísticas de planilla cerrada ID: ${planillaId}...`);
            
            const response = await api.get(`/planillas-cierre/${planillaId}/estadisticas`);

            if (response.data && response.data.data) {
                console.log(`✅ Estadísticas obtenidas:`, response.data.data);

                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || 'Estadísticas obtenidas exitosamente'
                };
            }

            return {
                success: false,
                error: 'No se encontraron estadísticas para la planilla'
            };

        } catch (error) {
            console.error('❌ Error al obtener estadísticas:', error);
            
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Error al obtener estadísticas';

            return {
                success: false,
                error: errorMessage,
                details: error.response?.data || null
            };
        }
    },

    /**
     * Verificar si una planilla ya está cerrada
     * @param {number} planillaId - ID de la planilla a verificar
     * @returns {Promise} - Estado del cierre de la planilla
     */
    async verificarEstadoCierre(planillaId) {
        try {
            console.log(`🔍 Verificando estado de cierre para planilla ID: ${planillaId}...`);
            
            // Intentamos obtener los datos de la planilla cerrada
            const resultado = await this.obtenerPlanillaCerrada(planillaId);
            
            if (resultado.success && resultado.data && resultado.data.length > 0) {
                const fechaCierre = resultado.data[0]?.fecha_cierre;
                const usuarioCierre = resultado.data[0]?.usuario_cierre;
                
                console.log(`✅ Planilla ${planillaId} está cerrada desde: ${fechaCierre}`);
                
                return {
                    success: true,
                    estaCerrada: true,
                    fechaCierre: fechaCierre,
                    usuarioCierre: usuarioCierre,
                    totalEmpleados: resultado.data.length
                };
            }

            console.log(`ℹ️ Planilla ${planillaId} no está cerrada`);
            return {
                success: true,
                estaCerrada: false
            };

        } catch (error) {
            console.error('❌ Error al verificar estado de cierre:', error);
            
            // Si hay error 404, probablemente la planilla no está cerrada
            if (error.response?.status === 404) {
                return {
                    success: true,
                    estaCerrada: false
                };
            }

            return {
                success: false,
                error: 'Error al verificar estado de cierre',
                details: error.response?.data || null
            };
        }
    },

    /**
     * Obtener resumen completo para mostrar antes del cierre
     * @param {number} planillaId - ID de la planilla a cerrar
     * @param {Array} empleadosCalculados - Datos de empleados ya calculados
     * @returns {Object} - Resumen para confirmación de cierre
     */
    async obtenerResumenParaCierre(planillaId, empleadosCalculados = []) {
        try {
            console.log(`📋 Preparando resumen para cierre de planilla ID: ${planillaId}...`);
            
            // Verificar si ya está cerrada
            const estadoCierre = await this.verificarEstadoCierre(planillaId);
            
            if (estadoCierre.estaCerrada) {
                return {
                    success: false,
                    error: 'La planilla ya está cerrada',
                    fechaCierre: estadoCierre.fechaCierre,
                    usuarioCierre: estadoCierre.usuarioCierre
                };
            }

            // Calcular resumen basado en empleados calculados
            const resumen = {
                planillaId: planillaId,
                totalEmpleados: empleadosCalculados.length,
                totalIngresos: 0,
                totalDescuentos: 0,
                totalAportes: 0,
                netoTotal: 0,
                conceptosCalculados: 0,
                empleadosConTareos: 0,
                empleadosConHorasExtras: 0,
                empleadosConCompensaciones: 0,
                empleadosConBoletasCompletas: 0
            };

            // Procesar datos de empleados calculados
            empleadosCalculados.forEach(empleado => {
                // Contar empleados con boletas calculadas completas
                if (empleado.boleta_calculada && empleado.boleta_calculada.success) {
                    resumen.empleadosConBoletasCompletas++;
                }

                // Analizar conceptos calculados
                if (empleado.conceptos) {
                    resumen.conceptosCalculados += empleado.conceptos.length;
                    
                    empleado.conceptos.forEach(concepto => {
                        const monto = parseFloat(concepto.monto) || 0;
                        
                        switch (concepto.tipo_concepto?.toLowerCase()) {
                            case 'ingreso':
                                resumen.totalIngresos += monto;
                                break;
                            case 'descuento':
                                resumen.totalDescuentos += monto;
                                break;
                            case 'aporte':
                                resumen.totalAportes += monto;
                                break;
                        }
                    });
                }

                // Contar empleados con tareos
                if (empleado.tareos && empleado.tareos.length > 0) {
                    resumen.empleadosConTareos++;
                }

                // Contar empleados con horas extras
                if (empleado.boleta_calculada?.data?.horasExtras && 
                    Object.keys(empleado.boleta_calculada.data.horasExtras).length > 0) {
                    resumen.empleadosConHorasExtras++;
                }

                // Contar empleados con compensaciones
                if (empleado.boleta_calculada?.data?.compensacionesMesesPasados?.horasEmpleado !== 0) {
                    resumen.empleadosConCompensaciones++;
                }
            });

            resumen.netoTotal = resumen.totalIngresos - resumen.totalDescuentos;

            console.log(`✅ Resumen preparado:`, resumen);

            return {
                success: true,
                data: resumen
            };

        } catch (error) {
            console.error('❌ Error al obtener resumen para cierre:', error);
            
            return {
                success: false,
                error: 'Error al preparar resumen para cierre',
                details: error
            };
        }
    },

        /**
     * Cerrar planilla con validación previa y resumen
     * @param {number} planillaId - ID de la planilla a cerrar
     * @param {Array} empleadosCalculados - Datos de empleados calculados (incluye configuracion_tareo en cada empleado)
     * @param {Object} opciones - Opciones adicionales para el cierre
     * @returns {Promise} - Resultado completo del cierre
     */
async cerrarPlanillaCompleta(planillaId, empleadosCalculados = [], opciones = {}) {
        try {
            console.log(`🔄 Iniciando cierre completo de planilla ID: ${planillaId}...`);
            console.log(`📊 Con ${empleadosCalculados.length} empleados calculados`);
            
            // Ejecutar el cierre enviando todos los datos calculados directamente
            const resultadoCierre = await this.cerrarPlanilla(
                planillaId,
                empleadosCalculados,
                true,
                opciones.onProgress || null
            );
            
            if (!resultadoCierre.success) {
                // Propagar errores del backend con toda la información
                return {
                    success: false,
                    error: resultadoCierre.error,
                    detalle_error: resultadoCierre.detalle_error,
                    data: resultadoCierre.data,
                    es_error_controlado: resultadoCierre.es_error_controlado,
                    http_status: resultadoCierre.http_status
                };
            }

            console.log(`✅ Cierre completo finalizado para planilla ${planillaId}`);

            return {
                success: true,
                data: resultadoCierre.data,
                estadisticas: resultadoCierre.estadisticas, // ✅ Propagar estadísticas
                message: resultadoCierre.message || 'Planilla cerrada exitosamente con todos los datos calculados capturados'
            };

        } catch (error) {
            console.error('❌ Error en cierre completo:', error);
            
            return {
                success: false,
                error: 'Error en el proceso completo de cierre',
                details: error
            };
        }
    },

    /**
     * Preparar datos de empleados para envío al backend
     * @param {Array} empleadosCalculados - Empleados con boletas calculadas (incluye configuracion_tareo en cada empleado)
     * @returns {Array} - Datos preparados para el backend
     */
    prepararDatosParaEnvio(empleadosCalculados) {
        return empleadosCalculados.map(empleado => {
            const boleta = empleado.boleta_calculada?.data || {};
            const boletaInterna = empleado.boleta_interna_calculada?.data || {};
            
            return {
                // Datos básicos del empleado
                empleado_id: empleado.id,
                nombre_completo: empleado.nombre_completo,
                numero_documento: empleado.numero_documento,
                tipo_planilla: empleado.tipo_planilla || null,
                sueldo_interno: empleado.sueldo_interno ?? null,
                
                // Conceptos calculados
                conceptos_calculados: empleado.conceptos || [],
                conceptos_internos_calculados: empleado.conceptos_internos || [],
                
                // Datos de la boleta PLAME
                ingresos: boleta.dataIngresos || [],
                descuentos: boleta.dataDescuentos || [],
                aportes: boleta.dataAportes || [],
                
                // Datos de la boleta interna (control)
                ingresos_internos: boletaInterna.dataIngresos || [],
                descuentos_internos: boletaInterna.dataDescuentos || [],
                aportes_internos: boletaInterna.dataAportes || [],
                
                // Totales
                total_ingresos: boleta.totales?.totalIngresos || 0,
                total_descuentos: boleta.totales?.totalDescuentos || 0,
                total_aportes: boleta.totales?.totalAportes || 0,
                neto_a_pagar: boleta.totales?.netoAPagar || 0,
                
                // Tareos
                tareos: empleado.tareos || [],
                
                // Horas extras
                horas_extras: boleta.horasExtras || {},
                
                // Compensaciones
                compensaciones: boleta.compensacionesMesesPasados || {},
                
                // ✅ CONFIGURACIÓN DE TAREOS INDIVIDUAL
                configuracion_tareo: empleado.configuracion_tareo || null,
                
                // Metadatos
                fecha_calculo: empleado.timestamp_calculo || new Date().toISOString(),
                estado_procesamiento: empleado.estado_procesamiento || 'calculado'
            };
        });
    },

    /**
     * Reabrir una planilla cerrada - Elimina los datos de cierre y cambia estado a proceso
     * @param {number} planillaId - ID de la planilla a reabrir
     * @param {string} comentarios - Comentarios opcionales sobre la reapertura
     * @returns {Promise} - Resultado de la reapertura
     */
    async reabrirPlanilla(planillaId, comentarios = '') {
        try {
            console.log(`🔓 Iniciando reapertura de planilla ID: ${planillaId}...`);
            console.log(`📝 Comentarios:`, comentarios);
            
            const payload = {
                comentarios: comentarios
            };
            
            console.log('📤 PAYLOAD REAPERTURA:', payload);
            
            const response = await api.post(`/planillas-cierre/${planillaId}/reabrir`, payload);

            // ✅ MANEJAR RESPUESTA DE ÉXITO
            if (response.data && response.data.success === true) {
                console.log(`✅ Planilla ${planillaId} reabierta exitosamente:`, response.data);
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || 'Planilla reabierta exitosamente',
                    estadisticas: response.data.estadisticas || response.data.data?.estadisticas || null
                };
            }

            // ❌ MANEJAR RESPUESTA DE ERROR DESDE EL BACKEND
            if (response.data && response.data.success === false) {
                console.error(`❌ Error en reapertura de planilla ${planillaId}:`, response.data);
                return {
                    success: false,
                    error: response.data.error || 'Error al reabrir planilla',
                    detalle_error: response.data.detalle_error || null,
                    data: response.data.data || null,
                    es_error_controlado: true
                };
            }

            // ❌ RESPUESTA INESPERADA
            console.error(`❌ Respuesta inesperada al reabrir planilla ${planillaId}:`, response.data);
            return {
                success: false,
                error: 'Respuesta inesperada del servidor',
                data: response.data || null,
                es_error_controlado: false
            };

        } catch (error) {
            console.error(`💥 Error inesperado al reabrir planilla ${planillaId}:`, error);
            
            // Manejar errores HTTP específicos
            if (error.response?.data) {
                return {
                    success: false,
                    error: error.response.data.error || 'Error al reabrir planilla',
                    detalle_error: error.response.data.detalle_error || error.message,
                    data: error.response.data.data || null,
                    es_error_controlado: error.response.data.success === false
                };
            }

            // Error de red u otro error inesperado
            return {
                success: false,
                error: 'Error de conexión al reabrir planilla',
                detalle_error: error.message,
                data: null,
                es_error_controlado: false
            };
        }
    }
};

export default planillaCierreService;

/*
================================================================================
EJEMPLOS DE USO DEL SERVICIO DE CIERRE DE PLANILLAS
================================================================================

📋 USO BÁSICO - Cerrar planilla con empleados calculados:

import planillaCierreService from './services/planillaCierreService';

// En tu componente PlanillaDetalle.js
const handleCerrarPlanilla = async () => {
    // Asegúrate de que empleadosConBoletasCalculadas esté disponible
    if (!empleadosConBoletasCalculadas || empleadosConBoletasCalculadas.length === 0) {
        alert('Debes calcular la planilla antes de cerrarla');
        return;
    }

    const resultado = await planillaCierreService.cerrarPlanillaCompleta(
        planillaReal.id, 
        empleadosConBoletasCalculadas // cada empleado incluye su configuracion_tareo
    );

    if (resultado.success) {
        alert('¡Planilla cerrada exitosamente!');
        console.log('Resultado completo:', resultado.data);
    } else {
        alert(`Error: ${resultado.error}`);
    }
};

--------------------------------------------------------------------------------

📊 VERIFICAR ESTADO ANTES DE MOSTRAR BOTÓN:

const [planillaCerrada, setPlanillaCerrada] = useState(false);

useEffect(() => {
    const verificarEstado = async () => {
        const estado = await planillaCierreService.verificarEstadoCierre(planillaReal.id);
        if (estado.estaCerrada) {
            setPlanillaCerrada(true);
        }
    };
    
    if (planillaReal?.id) {
        verificarEstado();
    }
}, [planillaReal?.id]);

// En el JSX
{!planillaCerrada && (
    <button onClick={handleCerrarPlanilla}>
        Cerrar Planilla
    </button>
)}

--------------------------------------------------------------------------------

📈 OBTENER DATOS HISTÓRICOS DE PLANILLA CERRADA:

const obtenerDatosHistoricos = async () => {
    const datos = await planillaCierreService.obtenerPlanillaCerrada(planillaId);
    
    if (datos.success) {
        console.log('Empleados cerrados:', datos.data);
        // datos.data es un array de empleados con toda la información:
        // - datos del empleado
        // - conceptos calculados 
        // - eventos de tareos
        // - horas extras
        // - totales
    }
};

--------------------------------------------------------------------------------

📋 OBTENER RESUMEN ANTES DEL CIERRE:

const mostrarResumenPreCierre = async () => {
    const resumen = await planillaCierreService.obtenerResumenParaCierre(
        planillaReal.id, 
        empleadosConBoletasCalculadas
    );

    if (resumen.success) {
        const data = resumen.data;
        console.log(`
            📊 RESUMEN PRE-CIERRE:
            Empleados: ${data.totalEmpleados}
            Total Ingresos: S/ ${data.totalIngresos}
            Total Descuentos: S/ ${data.totalDescuentos}
            Neto a Pagar: S/ ${data.netoTotal}
            Conceptos: ${data.conceptosCalculados}
            Con Tareos: ${data.empleadosConTareos}
            Con Horas Extras: ${data.empleadosConHorasExtras}
            Con Compensaciones: ${data.empleadosConCompensaciones}
        `);
    }
};

--------------------------------------------------------------------------------

🔧 PREPARAR DATOS PARA ENVÍO PERSONALIZADO:

// Si necesitas enviar los datos con formato específico
const datosPreparados = planillaCierreService.prepararDatosParaEnvio(empleadosConBoletasCalculadas);

const resultado = await planillaCierreService.cerrarPlanilla(
    planillaReal.id,
    datosPreparados,
    true
);

================================================================================
ESTRUCTURA DE DATOS ESPERADA
================================================================================

📊 FORMATO DE empleadosConBoletasCalculadas:

[
    {
        id: 123,
        nombre_completo: "Juan Pérez",
        numero_documento: "12345678",
        conceptos: [...],  // Array de conceptos calculados
        tareos: [...],     // Array de eventos de tareo
        boleta_calculada: {
            success: true,
            data: {
                dataIngresos: [...],    // Ingresos calculados
                dataDescuentos: [...],  // Descuentos calculados  
                dataAportes: [...],     // Aportes calculados
                totales: {
                    totalIngresos: 3500.00,
                    totalDescuentos: 850.00,
                    totalAportes: 315.00,
                    netoAPagar: 2650.00
                },
                horasExtras: {...},     // Horas extras por tipo
                compensacionesMesesPasados: {...}  // Compensaciones
            }
        },
        // ✅ CONFIGURACIÓN DE TAREOS INDIVIDUAL
        configuracion_tareo: {
            id: 1,
            empresa_id: 123,
            tipo_tareo: 2,
            descontar_descansos: true,
            modo_horas_simple: 'reales',
            es_simple: false,
            es_detallado: true,
            es_por_horas: false
        },
        estado_procesamiento: 'calculado',
        timestamp_calculo: '2025-01-29T15:30:00Z'
    }
    // ... más empleados
]

================================================================================
*/ 